import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { setSeo } from "@/lib/seo";

/**
 * Real "page not found" screen.
 *
 * WHAT THIS FIXES AND WHAT IT CANNOT FIX
 * The SEO audit's `t_soft404` finding is about the HTTP STATUS: any unknown
 * path used to answer 200 with the SPA shell (and this app previously silently
 * redirected unknown paths to "/", which is worse - it turns every typo into a
 * duplicate of the homepage).
 *
 * This component fixes the half that lives in the repo:
 *   * the URL is kept (no sneaky redirect to the homepage),
 *   * the page says plainly that nothing exists here,
 *   * `noindex, nofollow` is set, and the canonical is not rewritten to "/",
 *     so crawlers that execute JavaScript will drop the URL instead of
 *     indexing an endless set of empty pages,
 *   * useful onward links are offered instead of a dead end.
 *
 * It CANNOT, on its own, make the server answer 404 - static hosting serves
 * index.html for every path before React exists. See docs/http-and-headers.md
 * for the exact ingress/hosting rule required to return a true 404.
 */
export default function NotFound() {
  const location = useLocation();

  useEffect(() => {
    setSeo({
      title: "Page Not Found | Sponsor Me",
      description:
        "This page does not exist on Sponsor Me. Browse peer recovery sponsors, read about the platform, or contact the person who builds it.",
      path: location.pathname,
      index: false,
      schema: null,
    });
  }, [location.pathname]);

  return (
    <div className="bg-[#FBF9F5] min-h-[70vh] flex items-center" data-testid="not-found-page">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <span className="w-12 h-12 rounded-2xl bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center">
          <Compass className="w-6 h-6" aria-hidden="true" />
        </span>
        <p className="mt-5 text-sm font-bold uppercase tracking-[0.14em] text-[#1F6F78]">
          404 - Page not found
        </p>
        <h1
          data-testid="not-found-heading"
          className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1F2A2E]"
        >
          This page does not exist
        </h1>
        <p className="mt-4 text-[#4B5A60] leading-8">
          The address{" "}
          <code className="px-1.5 py-0.5 rounded bg-white border border-[#E3DED4] text-sm">
            {location.pathname}
          </code>{" "}
          does not match anything on Sponsor Me. It may have been mistyped, or the page may have
          been removed.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Button asChild className="bg-[#1F6F78] hover:bg-[#185B62] text-white h-11 px-6">
            <Link to="/sponsors" data-testid="not-found-sponsors-link">
              Browse peer sponsors <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-11 px-6 bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
          >
            <Link to="/" data-testid="not-found-home-link">
              Go to the homepage
            </Link>
          </Button>
        </div>
        <ul className="mt-8 grid gap-2 sm:grid-cols-2 text-sm">
          {[
            { to: "/about", label: "About Sponsor Me" },
            { to: "/pricing", label: "Pricing and plans" },
            { to: "/resources", label: "Recovery resources and crisis lines" },
            { to: "/contact", label: "Contact Sponsor Me" },
          ].map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="font-semibold text-[#1F6F78] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
